import styled from "@emotion/styled"
import { LikeTeamIntroductionModal } from "@pallet/features/RecruitingTeamType/LikeTeamAction/IntroductionModal"
import { MiniTeamProfile } from "@pallet/features/RecruitingTeamType/MiniTeamProfile"
import { MiniTeamProfileLoader } from "@pallet/features/RecruitingTeamType/MiniTeamProfile/Loader"
import { Box } from "@pallet-hq/hegel"
import { useMemo } from "react"
import InfiniteScroll from "react-infinite-scroller"
import LoadingIndicator from "components/LoadingIndicator"
import { useScreenSize } from "utils/hooks/useSize"
import { TeamsListEmptyState } from "./EmptyState"
import {
    LikableTeamsFragment,
    LikableTeamsFragment_edges_node,
} from "__generated__/LikableTeamsFragment"

type LikableTeamsListProps = {
    teamConnection?: LikableTeamsFragment
    loading: boolean
    fetchMore: (fetchMoreOptions: any) => void
    showPosts: boolean
}

const LikableTeamsList = ({
    teamConnection,
    loading,
    fetchMore,
    showPosts,
}: LikableTeamsListProps) => {
    const { isMobile } = useScreenSize()
    const teams: {
        leftTeams: LikableTeamsFragment_edges_node[]
        rightTeams: LikableTeamsFragment_edges_node[]
    } = useMemo(() => {
        const leftTeams: LikableTeamsFragment_edges_node[] = []
        const rightTeams: LikableTeamsFragment_edges_node[] = []
        teamConnection?.edges.map((team, index) => {
            index % 2 === 0 || isMobile
                ? leftTeams.push(team.node)
                : rightTeams.push(team.node)
        })
        return { leftTeams, rightTeams }
    }, [teamConnection, isMobile])

    const loadMore = () => {
        if (!teamConnection?.pageInfo.hasNextPage) return
        fetchMore({
            variables: {
                after: teamConnection.pageInfo.endCursor,
            },
        })
    }
    const showEmptyState = !loading && !teamConnection?.edges.length

    return (
        <>
            <InfiniteScroll
                loadMore={loadMore}
                hasMore={teamConnection?.pageInfo.hasNextPage}
                loader={
                    <Box m="large">
                        <LoadingIndicator />
                    </Box>
                }
                style={{ margin: "16px auto" }}
            >
                {showEmptyState && (
                    <TeamsListEmptyState hasError={!teamConnection} />
                )}
                <Box display="flex" gridGap="small">
                    <StyledColumn>
                        {loading && <MiniTeamProfileLoader />}
                        {teams.leftTeams.map(team => (
                            <MiniTeamProfile
                                key={team.id}
                                team={team}
                                showPosts={showPosts}
                            />
                        ))}
                    </StyledColumn>
                    {(loading ||
                        !teams.leftTeams.length ||
                        !!teams.rightTeams.length) && (
                        <StyledColumn>
                            {loading && <MiniTeamProfileLoader />}
                            {teams.rightTeams.map(team => (
                                <MiniTeamProfile
                                    key={team.id}
                                    team={team}
                                    showPosts={showPosts}
                                />
                            ))}
                        </StyledColumn>
                    )}
                </Box>
            </InfiniteScroll>
            <LikeTeamIntroductionModal />
        </>
    )
}

export { LikableTeamsList }

const StyledColumn = styled(Box)`
    display: flex;
    flex-direction: column;
    grid-gap: 10px;
    width: 100%;
`
